import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Eye, Bell, Film, TrendingUp, Sparkles, Shield, Check } from "lucide-react";
import ValuePropIllustration from "@/components/ValuePropIllustration";

export default function HowItWorksArticle() {
  useEffect(() => {
    document.title = "How Aavaa works — AI child monitoring, behavior tracking & daily summaries for parents";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", "A deep look at how Aavaa analyzes your child's day from an existing home camera: clip-backed observations, custom behaviors, early warning signs, monthly trends, and why it matters for parents of children with Down syndrome, autism and developmental differences.");
    return () => {
      document.title = "Aavaa — Your Child Development Companion | AI-powered daily summaries from your home camera";
    };
  }, []);

  const customizable = [
    "Throwing stuff or objects",
    "Crying frequency & duration",
    "Eating habits and mealtime cooperation",
    "Sleep duration and restlessness",
    "Tantrums and protest moments",
    "Play patterns & engagement",
    "Speech attempts and babbling",
    "Repetitive or self-stimulating movements",
    "Walking stability and gait",
    "Caregiver engagement quality",
    "Social interaction and eye contact",
    "Emotional state across the day",
    "Safety incidents and near-misses",
    "Screen time and passive watching",
    "Focus during one-to-one activities",
    "Developmental milestones emerging",
  ];

  return (
    <article className="min-h-screen bg-[#FAFAF7] text-[#2F3630]" data-testid="how-it-works-article">
      {/* Article structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "How Aavaa works — AI child monitoring from your home camera",
            description: "How Aavaa analyzes your child's day, generates clip-backed daily summaries, and lets parents customize exactly which behaviors and signals to monitor.",
            image: "https://aavaa.co/og-image.png",
            author: { "@type": "Organization", name: "Aavaa" },
            publisher: {
              "@type": "Organization",
              name: "Aavaa",
              logo: { "@type": "ImageObject", url: "https://aavaa.co/favicon.svg" },
            },
            datePublished: "2026-04-18",
            mainEntityOfPage: "https://aavaa.co/how-it-works",
          }),
        }}
      />
      {/* FAQ structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "What does Aavaa do?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Aavaa is an AI child development companion that connects to your existing home camera, quietly analyzes your child's day, and every evening gives you a warm, specific summary of what mattered — every observation backed by the actual video clip.",
                },
              },
              {
                "@type": "Question",
                name: "Do I need to buy a new camera?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "No. Aavaa is designed to work with compatible cameras you may already own (Tapo, Imou and others). A simple on-device Aavaa Hub pairs with your camera and handles the processing locally.",
                },
              },
              {
                "@type": "Question",
                name: "Can I choose what Aavaa monitors?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes. Parents fully customize what Aavaa looks for — from behaviors like throwing stuff or tantrums, to patterns like sleep, eating, focus, social interaction, caregiver engagement, aggressive behavior and developmental milestones.",
                },
              },
              {
                "@type": "Question",
                name: "Is Aavaa a good fit for children with special needs?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Aavaa was built first for parents of children with Down syndrome, autism and other developmental differences, because those parents need to notice a thousand tiny things every day — and no human can. Aavaa is designed to complement, not replace, caregiver attention.",
                },
              },
              {
                "@type": "Question",
                name: "What about privacy?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Aavaa is private by design. The Aavaa Hub processes video locally in your home. Only the daily summary — and the clips you choose to share — leave your home.",
                },
              },
            ],
          }),
        }}
      />

      {/* Header */}
      <header className="border-b border-[#E2DFD8]/50 bg-[#FAFAF7]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#6B7264] hover:text-[#2F3630] transition-colors" data-testid="article-back-home">
            <ArrowLeft size={16} />
            <span className="font-outfit text-sm font-medium">aavaa</span>
          </Link>
          <Link
            to="/#waitlist"
            className="rounded-full bg-[#8E9B82] hover:bg-[#7A8A6E] text-white px-4 py-2 font-outfit font-medium text-sm transition-colors"
            data-testid="article-request-demo-btn"
          >
            Request demo
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-16">
        {/* Hero */}
        <p className="font-manrope text-xs tracking-[0.15em] text-[#D4A373] uppercase mb-3">A deep look inside Aavaa</p>
        <h1 className="font-outfit text-4xl sm:text-5xl font-medium tracking-tight leading-[1.05] mb-5">
          How Aavaa watches your child's day — and turns it into something you can actually use.
        </h1>
        <p className="font-manrope text-lg text-[#6B7264] leading-relaxed mb-8">
          Aavaa is an AI child development companion for parents who want to know how their child's day <em>really</em> went — the cheerful moments, the concerning ones, and the tiny developmental wins that usually slip by unnoticed. This article walks you through exactly how it works, what it can track, and why it matters, especially for parents of children with Down syndrome, autism and other developmental differences.
        </p>

        <div className="mb-12 bg-white rounded-[24px] p-6 sm:p-8 border border-[#E2DFD8]/60">
          <ValuePropIllustration />
        </div>

        {/* Section: The day, analyzed */}
        <h2 className="font-outfit text-2xl sm:text-3xl font-medium tracking-tight mb-3 flex items-center gap-3">
          <span className="w-1.5 h-7 bg-[#D4A373] rounded-full" />
          The day, analyzed
        </h2>
        <p className="font-manrope text-[#6B7264] leading-relaxed mb-4">
          Aavaa connects to a compatible home camera (or an Aavaa-bundled one) and ingests short clips throughout the day. For every clip, it runs a hybrid AI pipeline: a video-understanding model watches for motion, emotion and behavior, while a vision model tracks precise objects — like whether your child is wearing her glasses, whether caregivers are on their phones, or whether a toy just got thrown.
        </p>
        <p className="font-manrope text-[#6B7264] leading-relaxed mb-8">
          At the end of the day, Aavaa stitches the whole thing into a single, warm summary — written in plain, parent-friendly language. You don't read data; you read a story about your child's day.
        </p>

        {/* Section: Timeline + Observations */}
        <h2 className="font-outfit text-2xl sm:text-3xl font-medium tracking-tight mb-3 flex items-center gap-3">
          <span className="w-1.5 h-7 bg-[#D4A373] rounded-full" />
          Timeline and observation cards
        </h2>
        <p className="font-manrope text-[#6B7264] leading-relaxed mb-4">
          Every summary has three parts — an Overview paragraph, a Timeline, and Observation cards:
        </p>
        <ul className="space-y-3 mb-8">
          {[
            { i: TrendingUp, t: "Overview", d: "One honest paragraph capturing the spirit of the day — mood, energy, notable high and low moments." },
            { i: Eye, t: "Timeline", d: "Chronological windows (e.g. 'Morning routines + mixed mood', 'Guided playroom time'), each with its own thumbnails of the clips that made up that window." },
            { i: Film, t: "Observation cards", d: "One card per question you chose to track. Every answer links back to the specific video clips it's based on — no vague claims, no hallucinations." },
          ].map((x, i) => (
            <li key={i} className="flex items-start gap-3 bg-white rounded-2xl p-4 border border-[#E2DFD8]/60">
              <div className="w-9 h-9 rounded-xl bg-[#8E9B82]/10 flex items-center justify-center flex-shrink-0">
                <x.i size={16} className="text-[#8E9B82]" />
              </div>
              <div>
                <p className="font-outfit text-base font-medium">{x.t}</p>
                <p className="font-manrope text-sm text-[#6B7264] leading-relaxed">{x.d}</p>
              </div>
            </li>
          ))}
        </ul>

        {/* Section: Customization */}
        <h2 className="font-outfit text-2xl sm:text-3xl font-medium tracking-tight mb-3 flex items-center gap-3">
          <span className="w-1.5 h-7 bg-[#D4A373] rounded-full" />
          Customize exactly what Aavaa tracks
        </h2>
        <p className="font-manrope text-[#6B7264] leading-relaxed mb-4">
          The most important thing about Aavaa: you — the parent — decide what matters. During onboarding you select from a library of behaviors, habits and developmental signals. You can also add your own, in your own words ("Did she try to say 'mama' again?", "Did he bang his head on the crib today?").
        </p>
        <p className="font-manrope text-[#6B7264] leading-relaxed mb-4">
          Examples of what Aavaa can monitor:
        </p>
        <div className="flex flex-wrap gap-2 mb-8">
          {customizable.map((c, i) => (
            <span key={i} className="bg-white border border-[#E2DFD8] rounded-full px-3.5 py-1.5 text-xs font-manrope text-[#6B7264]">
              {c}
            </span>
          ))}
        </div>

        {/* Section: Alerts */}
        <h2 className="font-outfit text-2xl sm:text-3xl font-medium tracking-tight mb-3 flex items-center gap-3">
          <span className="w-1.5 h-7 bg-[#D4A373] rounded-full" />
          Gentle alerts, not anxiety
        </h2>
        <p className="font-manrope text-[#6B7264] leading-relaxed mb-4">
          Some moments can't wait for evening. If Aavaa detects something you asked it to flag — repeated glasses removal, prolonged crying, a safety concern, an unusual pattern — it sends a push notification. The alert links straight to the 10-second clip that triggered it. You're never guessing.
        </p>
        <div className="bg-[#8E9B82]/8 border border-[#8E9B82]/20 rounded-2xl p-5 mb-8 flex items-start gap-3">
          <Bell size={18} className="text-[#8E9B82] mt-0.5" />
          <p className="font-manrope text-sm text-[#2F3630] leading-relaxed">
            Alerts are designed to be <strong>quiet and useful</strong>, not overwhelming. You choose which signals are urgent, which are for the daily summary only, and which Aavaa should ignore completely.
          </p>
        </div>

        {/* Section: Monthly trends */}
        <h2 className="font-outfit text-2xl sm:text-3xl font-medium tracking-tight mb-3 flex items-center gap-3">
          <span className="w-1.5 h-7 bg-[#D4A373] rounded-full" />
          Weekly and monthly trends
        </h2>
        <p className="font-manrope text-[#6B7264] leading-relaxed mb-8">
          Day-by-day is noise. Week-by-week is signal. Aavaa quietly aggregates your daily observations into weekly and monthly views — so you can see engagement rising, mobility stabilizing, meal cooperation trending up, or glasses-wearing consistency improving. For parents of children with developmental differences, this is often the single most motivating part of the product — <strong>progress, made visible</strong>.
        </p>

        {/* Section: Special needs */}
        <h2 className="font-outfit text-2xl sm:text-3xl font-medium tracking-tight mb-3 flex items-center gap-3">
          <span className="w-1.5 h-7 bg-[#D4A373] rounded-full" />
          Built first for parents of children with special needs
        </h2>
        <p className="font-manrope text-[#6B7264] leading-relaxed mb-4">
          Raising a child with Down syndrome, autism or other developmental differences means noticing a thousand tiny things every day — many of which matter deeply for therapists, pediatricians and early-intervention planning. No human can hold all of it. Aavaa was built to quietly carry that weight with you.
        </p>
        <p className="font-manrope text-[#6B7264] leading-relaxed mb-8">
          Therapists and caregivers can be added as collaborators, giving them access to summaries and relevant clips. What used to be hand-scribbled notes before a therapy session becomes a rich, shared record of the week — and your therapist walks in already informed.
        </p>

        {/* Section: Privacy */}
        <h2 className="font-outfit text-2xl sm:text-3xl font-medium tracking-tight mb-3 flex items-center gap-3">
          <span className="w-1.5 h-7 bg-[#D4A373] rounded-full" />
          Private by design
        </h2>
        <div className="bg-white rounded-2xl p-5 border border-[#E2DFD8]/60 mb-10 flex items-start gap-3">
          <Shield size={18} className="text-[#8E9B82] mt-0.5" />
          <p className="font-manrope text-sm text-[#2F3630] leading-relaxed">
            The Aavaa Hub processes video locally in your home. Only your daily summary and the clips you choose to share ever leave your home. No background uploads. No third-party ads. No surveillance.
          </p>
        </div>

        {/* FAQ */}
        <h2 className="font-outfit text-2xl sm:text-3xl font-medium tracking-tight mb-5 flex items-center gap-3">
          <span className="w-1.5 h-7 bg-[#D4A373] rounded-full" />
          Frequently asked
        </h2>
        <div className="space-y-3 mb-12">
          {[
            { q: "What does Aavaa do?", a: "Aavaa is an AI child development companion that connects to your existing home camera, quietly analyzes your child's day, and every evening gives you a warm, specific summary of what mattered — every observation backed by the actual video clip." },
            { q: "Do I need to buy a new camera?", a: "No. Aavaa is designed to work with compatible cameras you may already own (Tapo, Imou and others). A simple on-device Aavaa Hub pairs with your camera and handles the processing locally." },
            { q: "Can I choose what Aavaa monitors?", a: "Yes. Parents fully customize what Aavaa looks for — from behaviors like throwing stuff or tantrums, to patterns like sleep, eating, focus, social interaction, caregiver engagement, aggressive behavior and developmental milestones." },
            { q: "Is Aavaa a good fit for children with special needs?", a: "Aavaa was built first for parents of children with Down syndrome, autism and other developmental differences, because those parents need to notice a thousand tiny things every day — and no human can. Aavaa is designed to complement, not replace, caregiver attention." },
            { q: "What about privacy?", a: "Aavaa is private by design. The Aavaa Hub processes video locally in your home. Only the daily summary — and the clips you choose to share — leave your home." },
          ].map((f, i) => (
            <details key={i} className="bg-white rounded-2xl border border-[#E2DFD8]/60 p-5 group" data-testid={`article-faq-${i}`}>
              <summary className="font-outfit text-base font-medium cursor-pointer list-none flex items-center justify-between">
                {f.q}
                <Sparkles size={14} className="text-[#D4A373] group-open:rotate-45 transition-transform" />
              </summary>
              <p className="font-manrope text-sm text-[#6B7264] leading-relaxed mt-3">{f.a}</p>
            </details>
          ))}
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-br from-[#8E9B82] to-[#7A8A6E] text-white rounded-[28px] p-8 sm:p-10 text-center">
          <h3 className="font-outfit text-2xl sm:text-3xl font-medium tracking-tight mb-3">
            Want to see it on your own camera?
          </h3>
          <p className="font-manrope text-white/85 max-w-md mx-auto mb-6 leading-relaxed">
            We run personal demos with parents every week. Takes 15 minutes.
          </p>
          <Link
            to="/#waitlist"
            className="inline-flex items-center gap-2 rounded-full bg-white text-[#2F3630] h-11 px-6 font-outfit font-medium text-sm hover:bg-[#FAFAF7] transition-colors"
            data-testid="article-cta-request-demo"
          >
            <Check size={16} /> Request a demo
          </Link>
        </div>
      </main>

      <footer className="border-t border-[#E2DFD8]/60 mt-10">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="article-footer-home">
            <div className="w-1.5 h-5 rounded-full bg-[#D4A373]" />
            <span className="font-outfit text-base font-medium tracking-tight">aavaa</span>
          </Link>
          <span className="font-manrope text-xs text-[#6B7264]/70">© 2026 Aavaa</span>
        </div>
      </footer>
    </article>
  );
}
